import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Paper, Typography, Button, TextField, Stack, Chip, Divider, CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { api } from "../../../api";

/**
 * FEAT-022 / D4 Option B — gate a student's incident-tab (report history).
 *
 * Responders keep access to the student record but must be granted access to view report history
 * (request -> approver approves). Approver tiers (school/district/super admin) bypass and see it
 * directly; they also get an inline panel here to approve/deny pending responder requests.
 *
 * Wrap a student's incidents table:  <ReportHistoryGate studentId={id}><DataTableMD .../></ReportHistoryGate>
 */
const APPROVER_TIERS = ["schoolAdmin", "districtAdmin", "disclosureAdmin"];

const ReportHistoryGate = ({ studentId, children }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isApprover = APPROVER_TIERS.includes(user.permission);
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState({ allowed: false, status: "none" });
  const [requests, setRequests] = useState([]);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api("get", { studentId }, "studentaccessgrant");
    setAccess(res?.data?.access || { allowed: false, status: "none" });
    setRequests(res?.data?.requests || []);
    setLoading(false);
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const requestAccess = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    await api("post", { studentId, action: "request", reason }, "studentaccessgrant");
    setBusy(false);
    load();
  };

  const decide = async (accessorId, action) => {
    setBusy(true);
    await api("post", { studentId, action, accessorId }, "studentaccessgrant");
    setBusy(false);
    load();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
    );
  }

  // Pending requests panel (approvers only) — shown above the report history.
  const pending = requests.filter((r) => r.status === "requested");
  const approverPanel = isApprover && pending.length > 0 && (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "warning.main" }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Report-history access requests ({pending.length})
      </Typography>
      <Stack gap={1}>
        {pending.map((r) => (
          <Box key={r.accessorId} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Typography variant="body2">
              Responder <code>{r.accessorId}</code> — reason: {r.reason || "—"}
            </Typography>
            <Stack direction="row" gap={1}>
              <Button size="small" variant="contained" disabled={busy} onClick={() => decide(r.accessorId, "approve")}>Approve</Button>
              <Button size="small" color="error" disabled={busy} onClick={() => decide(r.accessorId, "deny")}>Deny</Button>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );

  if (access.allowed) {
    return (<>{approverPanel}{children}</>);
  }

  // Responder without access -> locked panel + request flow.
  return (
    <Paper variant="outlined" sx={{ p: 4, textAlign: "center", maxWidth: 560, mx: "auto", mt: 2 }}>
      <LockOutlinedIcon sx={{ fontSize: 40, color: "text.secondary" }} />
      <Typography variant="h6" sx={{ mt: 1 }}>Report history is restricted</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
        A student's report history is confidential. Request access with a reason; a school
        administrator will review it. Your request and its approval are recorded.
      </Typography>
      {access.status === "requested" ? (
        <Chip color="warning" label="Request pending approval" />
      ) : (
        <>
          {access.status === "denied" && (
            <Chip color="error" label="Previous request denied" sx={{ mb: 2 }} />
          )}
          <Divider sx={{ my: 2 }} />
          <Stack gap={1.5} sx={{ maxWidth: 420, mx: "auto" }}>
            <TextField
              label="Reason for access" size="small" multiline minRows={2}
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Reviewing prior reports as part of a student welfare check"
            />
            <Button variant="contained" disabled={busy || !reason.trim()} onClick={requestAccess}>
              Request access
            </Button>
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default ReportHistoryGate;
